import React from "react";
import { Truck, ShieldCheck, Banknote, Headphones, Star, Award, Clock, Phone } from "lucide-react";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLang } from "../context/LanguageContext";

const ICONS = { Truck, ShieldCheck, Banknote, Headphones, Star, Award, Clock, Phone };

export default function TrustBadges() {
  const { settings } = useSiteSettings() || {};
  const { lang, t } = useLang();

  // Translated defaults (fallback and AR auto-translate when admin hasn't set AR fields)
  const I18N_BY_ICON = {
    Truck: { label: t("trust.freeDelivery"), sub: t("trust.freeDeliverySub") },
    ShieldCheck: { label: t("trust.warranty"), sub: t("trust.warrantySub") },
    Banknote: { label: t("trust.cod"), sub: t("trust.codSub") },
    Headphones: { label: t("trust.support"), sub: t("trust.supportSub") },
  };

  const FALLBACK = [
    { icon: "Truck", ...I18N_BY_ICON.Truck },
    { icon: "ShieldCheck", ...I18N_BY_ICON.ShieldCheck },
    { icon: "Banknote", ...I18N_BY_ICON.Banknote },
    { icon: "Headphones", ...I18N_BY_ICON.Headphones },
  ];

  // If admin set custom badges, use them. In AR mode, prefer admin's *_ar fields;
  // if those are missing, fall back to our i18n dictionary when the icon is a known one.
  // In EN mode, always prefer our i18n dictionary for the 4 canonical trust icons
  // so stale CMS copy doesn't leak (e.g., "Cash on delivery" after we switched to
  // installation-only). Admin can still override via custom icons outside this set.
  let badges;
  if (settings?.trust_badges && settings.trust_badges.length > 0) {
    badges = settings.trust_badges.map((b) => {
      const dict = I18N_BY_ICON[b.icon];
      if (dict) {
        // Known icon → always use i18n dictionary so copy stays consistent across the app.
        return { icon: b.icon, label: dict.label, sub: dict.sub };
      }
      // Custom icon → honor admin values.
      const label = lang === "ar" ? b.label_ar || b.label : b.label;
      const sub = lang === "ar" ? b.sub_ar || b.sub : b.sub;
      return { icon: b.icon, label, sub };
    });
  } else {
    badges = FALLBACK;
  }

  return (
    <section data-testid="trust-badges" className="bg-white border-y border-black/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-black/10">
        {badges.map((b, idx) => {
          const Icon = ICONS[b.icon] || Truck;
          return (
            <div key={idx} className="px-4 md:px-8 py-8 flex items-center gap-4 hover:bg-secondary transition-colors">
              <div className="w-12 h-12 grid place-items-center border border-ink">
                <Icon size={20} />
              </div>
              <div>
                <div className="font-display font-extrabold text-base leading-none">{b.label}</div>
                <div className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">{b.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
